import React from 'react';
import { Check, ArrowRight, Sparkles } from 'lucide-react';
import type { Notification } from '../../types/notification';
import { getNotificationVisual, getNotificationLink } from './notificationConfig';
import { formatRelativeTime } from '../../utils/timeUtils';

export interface NotificationRowProps {
  notification: Notification;
  /** Density affects padding + typography. */
  density?: 'compact' | 'comfortable';
  /** Whether this row is the current keyboard-focused row. */
  focused?: boolean;
  /** Whether this row is currently selected in a bulk-select session. */
  selected?: boolean;
  /** Render the selection checkbox. */
  showSelect?: boolean;
  /** Called when the row (not a child action) is activated. */
  onActivate?: (notification: Notification) => void;
  /** Called when the user marks this notification read via the row action. */
  onMarkRead?: (notification: Notification) => void;
  /** Called when the select checkbox toggles. */
  onToggleSelect?: (notification: Notification) => void;
}

/**
 * Unified notification row — used by the NotificationDropdown and the
 * NotificationsPage so the two surfaces stay visually consistent.
 */
const NotificationRow: React.FC<NotificationRowProps> = ({
  notification,
  density = 'comfortable',
  focused = false,
  selected = false,
  showSelect = false,
  onActivate,
  onMarkRead,
  onToggleSelect,
}) => {
  const visual = getNotificationVisual(notification.type);
  const Icon = visual.icon;
  const link = getNotificationLink(notification);
  const isUnread = !notification.isRead;
  const isHero = !!visual.highlight;

  const padX = density === 'compact' ? 'px-4' : 'px-5';
  const padY = density === 'compact' ? 'py-3' : 'py-3.5';
  const titleSize = density === 'compact' ? 'text-13' : 'text-[13.5px]';
  const bodySize = density === 'compact' ? 'text-xs' : 'text-[12.5px]';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onActivate?.(notification);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={focused ? 'true' : undefined}
      aria-selected={selected || undefined}
      aria-label={`${visual.label}: ${notification.title}${isUnread ? ', unread' : ''}`}
      onClick={() => onActivate?.(notification)}
      onKeyDown={handleKeyDown}
      className={`
        group relative ${padX} ${padY} cursor-pointer outline-none
        transition-colors duration-150
        ${isHero && isUnread
          ? 'bg-gradient-to-r from-amber-50/80 via-amber-50/30 to-transparent border-l-[3px] border-l-amber-400'
          : ''}
        ${!isHero && isUnread ? 'bg-blue-50/40' : ''}
        ${isHero && !isUnread ? 'bg-amber-50/20' : ''}
        ${focused ? 'bg-ink-100 ring-2 ring-inset ring-blue-500/40' : ''}
        ${selected ? 'bg-blue-50/70' : ''}
        hover:bg-ink-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500/40
      `}
    >
      <div className="flex items-start gap-3">
        {showSelect && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggleSelect?.(notification);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            aria-pressed={selected}
            aria-label={selected ? 'Deselect notification' : 'Select notification'}
            className={`
              mt-1 flex-shrink-0 h-4 w-4 rounded border transition-colors
              flex items-center justify-center
              focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40
              ${selected
                ? 'bg-blue-600 border-blue-600 text-white'
                : 'bg-surface border-ink-300 hover:border-ink-400'}
            `}
          >
            {selected && <Check className="h-3 w-3" strokeWidth={3} />}
          </button>
        )}

        {/* Icon tile */}
        <div
          className={`relative flex-shrink-0 h-9 w-9 rounded-lg ${visual.tileBg} flex items-center justify-center`}
          aria-hidden="true"
        >
          <Icon className={`h-[18px] w-[18px] ${visual.tileIcon}`} />
          {isHero && (
            <Sparkles className="h-2.5 w-2.5 absolute -top-0.5 -right-0.5 text-amber-500" />
          )}
          {isUnread && !isHero && (
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 rounded-full ring-2 ring-white" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <p
              className={`${titleSize} leading-tight truncate ${isUnread ? 'font-semibold text-ink-900' : 'font-medium text-ink-700'}`}
            >
              {notification.title}
            </p>
            <span
              className="text-11 text-ink-400 tabular-nums flex-shrink-0 mt-0.5 whitespace-nowrap"
              title={notification.timestamp.toLocaleString()}
            >
              {formatRelativeTime(notification.timestamp, { shortFormat: true })}
            </span>
          </div>

          <p
            className={`${bodySize} mt-0.5 line-clamp-2 leading-relaxed ${isUnread ? 'text-ink-600' : 'text-ink-500'}`}
          >
            {notification.message}
          </p>

          <div className="flex items-center justify-between gap-2 mt-1.5">
            <div className="flex items-center gap-1.5 min-w-0">
              <span
                className={`inline-flex items-center text-11 font-medium rounded px-1.5 py-0.5 ${visual.pillBg} ${visual.pillText}`}
              >
                {visual.label}
              </span>
              {notification.priority === 'high' && !isHero && (
                <span className="inline-flex items-center gap-1 text-11 font-medium rounded px-1.5 py-0.5 bg-rose-50 text-rose-700">
                  Priority
                </span>
              )}
              {link && (
                <span className="hidden sm:inline-flex items-center gap-1 text-11 font-medium text-blue-600 group-hover:text-blue-700 transition-colors ml-1 truncate">
                  <span className="truncate">{link.label}</span>
                  <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5 flex-shrink-0" />
                </span>
              )}
            </div>

            {isUnread && onMarkRead && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkRead(notification);
                }}
                onKeyDown={(e) => e.stopPropagation()}
                className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 p-1 -mr-1 text-ink-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40"
                aria-label="Mark as read"
                title="Mark as read"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationRow;
